import { ChangeEvent, useEffect, useState} from "react";
import { LABELS } from "~/resources/Labels";
import { FormScriptResponse, FormScriptTypes, PropRow, SortTable } from "~/models/Commons ";
import {
  useDeleteScriptTypeMutation,
  useGetscriptTypesQuery,
} from "~/services/script";
import {
  attachResourcesToScriptsTypes,
  displayEntries,
  sortScripts,
} from "../../../Utils/Utils";
import { SIMPLTable } from "../../../components/commons/table/SimplTable";
import { Layout } from "../../../components/commons/layout/Layout";
import AddIcon from "@mui/icons-material/Add";
import { SIMPLButtonCustomized } from "../../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../../components/commons/dialog/SimplAlert";
import { SIMPLMenuHeader } from "../../../components/commons/header/SIMPLMenuHeader";
import { PayloadType } from "~/models/types/Payload";
import { Entries } from "~/models/enums/Entries";
import { LoadingWrapper } from "../../../components/commons/spiner/SimpleLoaderWrapper";
import { Alerts } from "../../messages/Alerts";
import { handleDeleteEntity } from "~/Utils/helpers/DeleteEntityHelper";
import { useTranslation } from "react-i18next";
import { ScriptsTypeHeader } from "~/models/scripts/ScriptsTypeHeader";
import AddScriptType from "./AddScriptType";


export const ScriptType = () => {
  const { t } = useTranslation();
  
  const [isAddScriptTypeModalOpen, setIsAddScriptTypeModalOpen] = useState(false);
  const [showScriptTypeFilter, setShowScriptTypeFilter] = useState<boolean>(false);
  const [scriptTypeRows, setScriptTypeRows] = useState<PropRow[]>([]);
  const [selectedScriptType, setSelectedScriptType] = useState({} as FormScriptTypes);
  const [isDeleteScriptTypeModalOpen, setIsDeleteScriptTypeModalOpen] = useState(false);
  const [isScriptTypeActive, setIsScriptTypeActive] = useState(true);
  const [scriptTypeTotalElements, setScriptTypeTotalElements] = useState<number>(0);

  const [deleteScriptType] = useDeleteScriptTypeMutation();

  const [scriptTypePageSize, setScriptTypePageSize] = useState<number>(5);
  const [scriptTypePage, setScriptTypePage] = useState<number>(0);
  const [scriptTypeTotalPages, setScriptTypeTotalPages] = useState<number>(0);
  const [showScriptTypePagination, setShowScriptTypePagination] = useState(true);

  const [showScriptTypeSuccessModal, setShowScriptTypeSuccessModal] = useState(false);
  const [showScriptTypeErrorModal, setShowScriptTypeErrorModal] = useState(false);

  type ResponseDetails = { title: string; message: string };
  const [responseDetails, setResponseDetails] = useState<ResponseDetails | null>(null);

  const { data, refetch, isLoading, isFetching, isError } =  useGetscriptTypesQuery({
    page: 0,
    size: 999,
    active: isScriptTypeActive,
  });

  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });

  // Renamed methods to avoid duplication
  const initScriptTypeData = () => {
    const payload = data?.payload as PayloadType | undefined;
    if (!payload || payload.length === 0) {
      setShowScriptTypePagination(false);
      return;
    }

    const scriptMergedList = attachResourcesToScriptsTypes(payload);
    const sortedScripts = sortScripts(scriptMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, scriptTypeTotalElements, 999);

    setScriptTypeTotalPages(payload?.page?.totalPages ?? 0);
    if (payload?.page?.totalElements) {
      setScriptTypeTotalElements(payload.page.totalElements);
    }

    const scriptRows: PropRow[] = customizedList.map((scriptItem: any) => ({
      row: scriptItem,
    }));

    setScriptTypeRows(scriptRows);
    setShowScriptTypePagination(true);
  };

  useEffect(() => {
    initScriptTypeData();
  }, [data, sortConfig, scriptTypePageSize, scriptTypeTotalPages, scriptTypeTotalElements]);

  useEffect(() => {
    setScriptTypePage(0);
    handleScriptTypePaginationSize();
  }, [scriptTypePageSize]);

  const handleScriptTypePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(scriptTypePageSize)) {
      setScriptTypePage(0);
    }
  };

  const toggleScriptTypeFilter = () => setShowScriptTypeFilter((prev) => !prev);
  const closeAddScriptTypeModal = () => setIsAddScriptTypeModalOpen(false);

  const handleScriptTypeSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setScriptTypePageSize(Number(e.target.value));
  };

  const resetScriptTypeAll = () => {
    setScriptTypePage(0);
    setShowScriptTypeFilter(false);
  };

  const handleScriptTypePageChange = (page: number) => {
    setScriptTypePage(page);
  };

  const handleScriptTypeSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === LABELS.ASC
      ? LABELS.Desc
      : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  const renderButtonAddScriptType = () => (
    <SIMPLButtonCustomized
      label={t('AddScriptType')}
      buttonHandlerAction={() => setIsAddScriptTypeModalOpen(true)}
      secondIcon={<AddIcon fontSize="small" />}
      primary
    />
  );

  const renderDeleteAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t('Cancel')}
          secondary
          buttonHandlerAction={() => setIsDeleteScriptTypeModalOpen(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t('Delete')} 
          danger
          buttonHandlerAction={handleDeleteScriptTypeItem}
        />
      </div>
    </>
  );

  const renderDeleteScriptTypeAlert = () =>
    isDeleteScriptTypeModalOpen && (
      <SIMPLAlert
        onCancel={() => setIsDeleteScriptTypeModalOpen(false)}
        message={t('MessageDialogDelete', { item: selectedScriptType.name })}
        title={t('DeleteScriptType')}
        actions={renderDeleteAlertButtons()}
      />
    );

  const handleConfirmDeleteScriptType = (scriptItem: FormScriptTypes) => {
    if (scriptItem.id) {
      setSelectedScriptType(scriptItem);
      setIsDeleteScriptTypeModalOpen(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleDeleteScriptTypeItem = async () => {
    handleDeleteEntity({
      deleteFn: () => deleteScriptType(selectedScriptType.id).unwrap(),
      id: selectedScriptType.id,
      refetchFn: refetch,
      onBeforeDelete: () => setIsDeleteScriptTypeModalOpen(false),
      onSuccess: (title, message) => {
        setResponseDetails({ title, message });
        setShowScriptTypeSuccessModal(true);
        setShowScriptTypeErrorModal(false);
      },
      onError: (title, message) => {
        setResponseDetails({ title, message });
        setShowScriptTypeErrorModal(true);
        setShowScriptTypeSuccessModal(false);
      },
      t
    });
  };

  const renderAlerts = () => (
    <Alerts
      showSuccess={showScriptTypeSuccessModal}
      showError={showScriptTypeErrorModal}
      title={responseDetails?.title}
      successMessage={responseDetails?.message || ""}
      errorMessage={responseDetails?.message || ""}
      onCloseSucessDialog={() => setShowScriptTypeSuccessModal(false)}
      onCloseErrorDialog={() => setShowScriptTypeErrorModal(false)}
      enabledSuccess={showScriptTypeSuccessModal}
      enabledError={showScriptTypeErrorModal}
    />
  );

  const renderScriptTypeTable = () => (
    <SIMPLTable
      title={t('ScriptType')}
      custumizedNoDataAvailable={t('NoDataAvailable')}
      clear={resetScriptTypeAll}
      page={scriptTypePage}
      size={scriptTypePageSize}
      showPagination={showScriptTypePagination}
      active={true}
      showHandler={toggleScriptTypeFilter}
      filterActive={showScriptTypeFilter}
      tableHeaders={ScriptsTypeHeader}
      rows={scriptTypeRows}
      onChangePage={handleScriptTypePageChange}
      onChange={handleScriptTypeSizeChange}
      button={renderButtonAddScriptType()}
      sortConfig={sortConfig}
      handleSort={handleScriptTypeSort}
      defaultSortKey="name"
      chipLabels={[]}
      handleDelete={handleConfirmDeleteScriptType}
      showButtonDeleteInModal
      showButtonDetailsInModal={false}
      showButtonDownloadInModal={false}
      showButtonDecommissionInModal={false}
      showReviewOutputTemplateInModal={false}
      showEditInModal={false}
      showCopyInModal={false}
      showConfigFileInModal={false}
      handlerChips={() => {}}
      setIsActive={setIsScriptTypeActive}
      isActive={isScriptTypeActive}
      activeLabel={t('Active')}
      inactiveLabel={t('Inactive')}
      tabs
    />
  );

  return (
    <SIMPLMenuHeader>
      {renderAlerts()}
      <Layout>
        {renderDeleteScriptTypeAlert()}

        {isAddScriptTypeModalOpen && (
          <AddScriptType
            onCloseModal={closeAddScriptTypeModal}
            editedItem={null}
            setConfig={setSortConfig}
            title={t('AddScriptType')}
            refetch={refetch}
            onSuccess={(title, message) => {
              setResponseDetails({ title, message });
              setShowScriptTypeSuccessModal(true);
              setShowScriptTypeErrorModal(false);
            }}
            onError={(title, message) => {
              setResponseDetails({ title, message });
              setShowScriptTypeErrorModal(true);
              setShowScriptTypeSuccessModal(false);
            }}
          />
        )}

        <LoadingWrapper
          loadingTitle={t('LoadingScripts')}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          {renderScriptTypeTable()}
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};